import { useScrAiSummary } from '@/hooks/scr-ai-summary'
import { useToast } from '@/hooks/use-toast'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { usePosthogCustom } from '@/hooks/usePosthogCustom'
import { hasDecisionForEveryPendingTreatment } from '@/lib/med-requests'
import { resetMedicationRequestStore, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { checkMedRequestActionLengths } from '@/lib/utils'
import { queryClient } from '@/services/queryClient'
import useMedRequestAction, { invalidateRelevantActionQueries } from '../action'
import { runMedRequestActionChain } from '../mps-family-actions'
import { multiProductOrderTreatmentDecisionActions } from './family-actions'

/** Submit decision: sends the decisions recorded on the cards, one at a time. */
export default function useSubmitTreatmentDecisions() {
  const medRequest = useCurrentMedication()
  const goNextMedRequest = useGoNextMedRequest()
  const { trackMedRequestAction } = usePosthogCustom()
  const { toast } = useToast()
  const { prompt: scrExecutedPrompt, summary: scrSummaryResult } = useScrAiSummary({
    medicationRequestId: medRequest?.data?.id ?? ''
  })

  // The other cards' edits live in the store, so it's only reset once nothing is left to decide.
  const action = useMedRequestAction({ enabled: true, doNotResetStore: true })

  const submit = async (): Promise<boolean> => {
    const data = medRequest?.data
    const state = useMedicationRequestStore.getState()
    const { treatmentDecisions, setTreatmentDecisionSubmitting } = state
    if (!data || state.isTreatmentDecisionSubmitting) return false
    // Sending part of the order would still move on to the next request.
    if (!hasDecisionForEveryPendingTreatment(data, treatmentDecisions)) return false

    const actions = multiProductOrderTreatmentDecisionActions(data, treatmentDecisions, {
      opened: {
        medication: state.medication,
        medicationDirection: state.medicationDirection,
        medicationIssueDate: state.medicationIssueDate,
        notes: state.notes,
        scrExecutedPrompt,
        scrSummaryResult
      },
      treatments: state.multiProductOrderTreatments
    })

    if (!actions) {
      toast({
        title: 'Decisions not sent',
        description: 'An approved treatment needs a medication and directions. Undo its decision to edit it.'
      })
      return false
    }
    if (!actions.length) return false

    // Checked up front so the API can't refuse one after others were sent.
    if (!actions.every(checkMedRequestActionLengths)) return false

    setTreatmentDecisionSubmitting(true)
    let chain: Awaited<ReturnType<typeof runMedRequestActionChain>>
    try {
      chain = await runMedRequestActionChain(action.mutateAsync, actions)

      if (!chain.ok) {
        // The queue row of the failed action was optimistically removed.
        invalidateRelevantActionQueries(data.patient.id ?? '', data.id)
        // Stays submitting until the refetch lands, or the sent decisions could be submitted again.
        await queryClient.refetchQueries({ queryKey: ['medicationRequestById', data.id] }, { cancelRefetch: false })
      }
    } finally {
      setTreatmentDecisionSubmitting(false)
    }

    const sent = actions.slice(0, chain.completed)
    for (const { id } of sent) trackMedRequestAction(treatmentDecisions[id].action, id)

    if (!chain.ok) {
      // The sent decisions now show as the treatments' own; the rest stay recorded for a retry.
      const { undoTreatmentDecision } = useMedicationRequestStore.getState()
      for (const { id } of sent) undoTreatmentDecision(id)

      // The mutation's own error toast explains a lone failure.
      if (chain.total > 1) {
        toast({
          title: 'Decisions incomplete',
          description: `${chain.completed} of ${chain.total} decisions were submitted; the rest are still pending. Please try again.`
        })
      }
      return false
    }

    resetMedicationRequestStore()
    goNextMedRequest()
    return true
  }

  return { submit }
}
